import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  BALL_IN_COURT,
  BALL_IN_COURT_LABELS,
  OPTIONAL_PIPELINE_STAGES,
  type ApplicationStage,
  type PipelineConfig,
  type PipelineStageConfig,
} from '@nest-recruit/shared';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { useModule, useStageLabels } from '../../../lib/modules';
import styles from './StageBuilder.module.css';

/**
 * Step 4 and ADMIN-01 share this (master plan S11). The route is fixed — product
 * owner, 2026-09-28 (Q1): the stages keep the order the application state machine
 * moves candidates through, and a job switches the optional ones on or off and
 * renames any of them. Nothing here reorders.
 *
 * The candidate-facing name is what the candidate sees in every message (§L21), and
 * internal names never reach them. "On hold" deliberately reads "Under review".
 *
 * A stage that is off keeps its names, dimmed, so switching it back on brings them back (Pipelines A, Admin redesign
 * batch 2); they can still be edited.
 *
 * A switched-off module's stages are not drawn (§L28): the test stage while Assessments is off,
 * and "Hired, not yet joined" and "Joined" while Offers is off (DECISIONS-LOG 2026-10-19 Q2).
 * Their saved names stay as they are.
 */

const ROUND_ORDER: readonly ApplicationStage[] = ['interview_1', 'interview_2', 'interview_3'];
/** Offers' stages: a hire is confirmed and joins through the offer (R8). */
const OFFER_STAGES: readonly ApplicationStage[] = ['hired_pending_join', 'joined'];

/** The stages drawn while these modules are switched as they are (§L28). The pipeline list's route dots use it too. */
export function shownStagesOf<T extends { readonly stage: ApplicationStage }>(
  stages: readonly T[],
  modules: { readonly isAssessmentsOn: boolean; readonly isOffersOn: boolean },
): readonly T[] {
  return stages.filter(
    (stage) => (modules.isAssessmentsOn || stage.stage !== 'assessment') && (modules.isOffersOn || !OFFER_STAGES.includes(stage.stage)),
  );
}

interface StageBuilderProps {
  readonly value: PipelineConfig;
  readonly onChange: (next: PipelineConfig) => void;
  /** Stages a candidate has entered: can't be switched off, and changes make a new version. */
  readonly lockedStages?: ReadonlySet<ApplicationStage>;
  readonly isReadOnly?: boolean;
  /**
   * Stages switched on and off somewhere else on the screen, with where: the stage's tick shows
   * that setting, read only (the job builder's test stage follows "Candidates take a test").
   */
  readonly followedStages?: Readonly<Partial<Record<ApplicationStage, string>>>;
  /**
   * ADMIN-01 (Pipelines A): one row per stage under one set of column headings, while the builder is wide enough; a
   * card per stage below that. The job builder keeps the cards.
   */
  readonly layout?: 'cards' | 'table';
}

export function StageBuilder({
  value,
  onChange,
  lockedStages = new Set(),
  isReadOnly = false,
  followedStages = {},
  layout = 'cards',
}: StageBuilderProps): ReactNode {
  const stageLabels = useStageLabels();
  // R1 (§L34): time limits belong to SLA timers; off, the column goes and the row closes up.
  const isSlaOn = useModule('sla');
  const shownStages = shownStagesOf(value.stages, { isAssessmentsOn: useModule('assessments'), isOffersOn: useModule('offers') });
  function setStage(stage: ApplicationStage, patch: Partial<PipelineStageConfig>): void {
    onChange({
      ...value,
      stages: value.stages.map((current) => {
        if (current.stage === stage) return { ...current, ...patch };
        // Rounds run in order: a later round switches the earlier ones on, and dropping
        // a round drops those after it, rather than a validation error to fix by hand.
        const switched = ROUND_ORDER.indexOf(stage);
        const position = ROUND_ORDER.indexOf(current.stage);
        if (switched < 0 || position < 0) return current;
        if (patch.isActive === true && position < switched) return { ...current, isActive: true };
        if (patch.isActive === false && position > switched) return { ...current, isActive: false };
        return current;
      }),
    });
  }

  return (
    <div className={styles.builder} data-layout={layout} data-sla={isSlaOn || undefined}>
      {layout === 'table' && (
        <div className={styles.tableHead} aria-hidden>
          <span>#</span>
          <span>Stage</span>
          <span>Use it</span>
          <span>Name for your team</span>
          <span>Name candidates see</span>
          {isSlaOn && <span>Time limit</span>}
          <span>Whose move</span>
        </div>
      )}
      <ol className={styles.stages} aria-label="Stages, in the order candidates move through them">
        {shownStages.map((stage, index) => {
          const isOptional = OPTIONAL_PIPELINE_STAGES.includes(stage.stage);
          const isLocked = lockedStages.has(stage.stage);
          const followed = followedStages[stage.stage];
          const stageName = stageLabels[stage.stage];
          return (
            <li key={stage.stage} className={styles.stage} data-off={!stage.isActive || undefined}>
              <div className={styles.stageHead}>
                <span className={styles.order} aria-hidden>
                  {index + 1}
                </span>
                <span className={styles.stageTitle}>
                  <span className={styles.stageName}>{stageName}</span>
                  {isLocked && (
                    <Chip tone="attention" plain>
                      Candidates have reached this stage
                    </Chip>
                  )}
                </span>
                <span className={styles.toggle}>
                  {isOptional ? (
                    <span className={styles.toggleWith}>
                      {followed !== undefined && <span className={styles.followed}>{followed}</span>}
                      <Checkbox
                        checked={stage.isActive}
                        disabled={isReadOnly || followed !== undefined || (isLocked && stage.isActive)}
                        label={stage.isActive ? 'On' : 'Off'}
                        ariaLabel={`Use the ${stageName} stage`}
                        onChange={(checked) => setStage(stage.stage, { isActive: checked })}
                      />
                    </span>
                  ) : (
                    <span className={styles.always}>
                      <Lock size={14} aria-hidden />
                      Always on
                    </span>
                  )}
                </span>
              </div>
              <div className={styles.stageFields}>
                <label className={styles.cell}>
                  <span className={styles.cellLabel}>Name for your team</span>
                  <Input
                    disabled={isReadOnly}
                    value={stage.internalName}
                    aria-label={`${stageName}: name for your team`}
                    isInvalid={stage.internalName.trim() === ''}
                    onChange={(event) => setStage(stage.stage, { internalName: event.target.value })}
                  />
                </label>
                <label className={styles.cell}>
                  <span className={styles.cellLabel}>Name candidates see</span>
                  <Input
                    disabled={isReadOnly}
                    value={stage.candidateFacingName}
                    aria-label={`${stageName}: name candidates see`}
                    isInvalid={stage.candidateFacingName.trim() === ''}
                    onChange={(event) => setStage(stage.stage, { candidateFacingName: event.target.value })}
                  />
                </label>
                {isSlaOn && (
                  <label className={styles.cell}>
                    <span className={styles.cellLabel}>Time limit</span>
                    <Input
                      isNumeric
                      inputMode="numeric"
                      suffix="working h"
                      placeholder="None"
                      disabled={isReadOnly}
                      aria-label={`${stageName}: time limit in working hours`}
                      value={stage.slaHours === null ? '' : String(stage.slaHours)}
                      onChange={(event) => setStage(stage.stage, { slaHours: hoursOrNull(event.target.value) })}
                    />
                  </label>
                )}
                <div className={styles.cell}>
                  <span className={styles.cellLabel}>Whose move</span>
                  <Select
                    label={`Whose move at ${stageName}`}
                    disabled={isReadOnly}
                    value={stage.ballInCourt}
                    options={BALL_IN_COURT.map((ball) => ({ value: ball, label: BALL_IN_COURT_LABELS[ball] }))}
                    onChange={(next) => {
                      const ball = BALL_IN_COURT.find((candidate) => candidate === next);
                      if (ball) setStage(stage.stage, { ballInCourt: ball });
                    }}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <section className={styles.outcomes} aria-labelledby="outcome-names">
        <h3 id="outcome-names" className={styles.heading}>
          What candidates see when they leave the route
        </h3>
        <p className={styles.hint}>
          On hold reads as “Under review” on purpose — a candidate is never told they are the backup.
        </p>
        <div className={styles.outcomeGrid}>
          {value.outcomes.map((outcome) => (
            <label key={outcome.stage} className={styles.cell}>
              <span className={styles.cellLabel}>{stageLabels[outcome.stage]}</span>
              <Input
                disabled={isReadOnly}
                value={outcome.candidateFacingName}
                isInvalid={outcome.candidateFacingName.trim() === ''}
                onChange={(event) =>
                  onChange({
                    ...value,
                    outcomes: value.outcomes.map((current) =>
                      current.stage === outcome.stage ? { ...current, candidateFacingName: event.target.value } : current,
                    ),
                  })
                }
              />
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

function hoursOrNull(text: string): number | null {
  const digits = text.replace(/\D/g, '');
  return digits === '' ? null : Number(digits);
}
